import { useNavigate } from "react-router-dom-v5-compat";

import { FetchParentInformationResponse } from "@seneca/parents-service-types";
import {
  Box,
  Button,
  Inline,
  Link,
  Stack,
  Text,
  useTheme,
  Var
} from "@seneca/senekit";
import { ExposeRecursively } from "@seneca/small-utils-library";

import { ReactComponent as BlackboardImg } from "common/assets/images/sign-up/passwordless/blackboard.svg";
import { termsAndConditionsLink } from "common/links";

import {
  requestSignInViaEmailLink,
  SignInViaEmailPreconnectedParentMeta
} from "services/firebase/auth";
import { logRequestSignInWithEmailLink } from "services/firebase/auth/analytics/signInViaEmailLink";

import { useTypedSearchParams } from "seneca-common/utils/routing/react-router-typesafe-routes/dom";

import { getAppName } from "features/common/utils/functions/app";
import { linkToSignIn } from "features/sign-up/links";
import { ROUTES } from "routing/routes";

import { PARENT_PRECONNECTED_SIGN_IN_CALLBACK_URL } from "./consts";

type Props = {
  data: ExposeRecursively<{ email: string } & FetchParentInformationResponse>;
};

export default function SignUpPreconnectedParent({ data }: Props) {
  const [{ connectionKey }] = useTypedSearchParams(ROUTES.SIGN_UP_PARENT);

  const { email, familyName, givenName } = data;

  const navigate = useNavigate();

  const onSignUpClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();

    logRequestSignInWithEmailLink({
      email
    });

    navigate(ROUTES.SIGN_UP_PARENT.CHECK_YOUR_EMAIL.buildPath({}), {
      state: { email, givenName, familyName }
    });

    await requestSignInViaEmailLink<SignInViaEmailPreconnectedParentMeta>({
      email: email,
      callbackUrl: PARENT_PRECONNECTED_SIGN_IN_CALLBACK_URL,
      metadata: data
    });
  };

  return (
    <Box
      component="form"
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box width="100%" marginBottom="xxlarge">
        <Stack align="center" space="xlarge">
          <Text>
            Click below to create an account for{" "}
            <strong>
              <Var>{email}</Var>
            </strong>
          </Text>

          <BlackboardImg />
        </Stack>
      </Box>
      <Stack align="center" space="xlarge">
        <Stack align="center" space="large">
          <Button onClick={onSignUpClick} width="100%">
            Sign up with e-mail link
          </Button>
          <Text align="center" size="small" emphasis="medium">
            By signing up you agree to {getAppName()}'s{" "}
            <Link
              tone="neutral"
              fontWeight="normal"
              emphasis="medium"
              to={termsAndConditionsLink("en")}
            >
              Terms & Conditions
            </Link>
          </Text>
        </Stack>

        <Inline alignItems="center" space="large">
          <Link
            to={ROUTES.SIGN_UP_PARENT.buildPath(
              {},
              {
                showPasswordSignUp: true,
                connectionKey
              }
            )}
            state={ROUTES.SIGN_UP_PARENT.buildState({ email })}
            size="small"
          >
            Use password instead
          </Link>

          <DotDivider />

          <Link to={linkToSignIn()} size="small">
            Log in
          </Link>
        </Inline>
      </Stack>
    </Box>
  );
}

function DotDivider() {
  const theme = useTheme();
  return (
    <Box
      backgroundColor={theme["neutral-xLow-content"]}
      borderRadius="xxxlarge"
      height="0.3rem"
      width="0.3rem"
      marginTop="xsmall"
    />
  );
}
